import { useTranslation } from "react-i18next";

import type { Item } from "../../../../api/menu";
import { useIsEdgeLocal } from "../../../../hooks/useEdgeMode";
import { useRecipeIngredientLines } from "../../../../hooks/useMenu";
import { useAppSelector } from "../../../../store/hooks";
import {
  IngredientLine,
  IngredientLineSkeleton,
  InfoEyebrow,
} from "./itemInfoParts";
import { formatIngredientQuantity } from "./recipeIngredientFormat";

interface RecipeIngredientsSectionProps {
  item: Item;
  open: boolean;
}

const SKELETON_ROWS = [0, 1, 2];

/**
 * „Съставки" в прозореца „Инфо" — общ за „Маркиране" и кухненските монитори
 * (`ItemDetailModal`). Само в режим със склад (`uses_warehouse`): тогава
 * продаваемото е рецепта и тук са съставките ѝ на ЕДНО ниво (под-рецептата е
 * един ред с бадж „Рецепта"). Без склад секцията я няма — модалът е както преди.
 * Без интернет касата връща последния огледан отговор за рецептата или 503 →
 * подсказка „Недостъпно без интернет" (без toast); друга грешка крие секцията.
 */
export const RecipeIngredientsSection = ({
  item,
  open,
}: RecipeIngredientsSectionProps) => {
  const { t, i18n } = useTranslation();
  const usesWarehouse = useAppSelector(
    (state) => state.auth.user?.uses_warehouse === true,
  );
  const isEdgeLocal = useIsEdgeLocal();
  // Обедното копие носи синтетично (отрицателно) id — рецептата е sourceItemId
  const recipeId = item.sourceItemId ?? item.id;
  const {
    data: lines,
    isLoading,
    isError,
  } = useRecipeIngredientLines(recipeId, i18n.language, open && usesWarehouse);

  if (!usesWarehouse) return null;
  if (isError && !isEdgeLocal) return null;
  if (!isLoading && !isError && (!lines || lines.length === 0)) return null;

  return (
    <section className="flex flex-col gap-1">
      <InfoEyebrow>{t("ordering.infoIngredients")}</InfoEyebrow>
      {isError ? (
        <p className="m-0 py-1.5 text-[13px] font-medium text-pos-muted">
          {t("edge.unavailableOffline")}
        </p>
      ) : isLoading ? (
        SKELETON_ROWS.map((row) => <IngredientLineSkeleton key={row} />)
      ) : (
        lines?.map((line, index) => (
          // Една заготовка може да е на два реда — индексът пази ключа уникален
          <IngredientLine
            key={`${line.kind}-${line.id}-${index}`}
            name={line.name}
            badge={line.kind === "recipe" ? t("ordering.infoSubRecipe") : undefined}
            value={formatIngredientQuantity(line)}
            unit={line.unit}
          />
        ))
      )}
    </section>
  );
};
